import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import { getMappedBusinessEntityIDs } from "../../../utils/utils";
import dayjs from "dayjs";
import {
  Autocomplete,
  Button,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

// Filter used for both NII Sensitivity and Advanced NII Sensitivity
const NIISensitivityModelerGlobalFilters = ({
  toCompare = false,
  setIsAdvancedNII,
  isAdvancedNII,
  setIsWritableSfc,
  handleSubmit,
}) => {
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    selectedModelDatabase,
    setModelDatabases,
    setSelectedModelDatabase,
    modelDatabases,
    setSelectedMonthEnd,
    setLocalCurrency,
    selectedFigureDivisor,
    selectedDecimalsIn,
    hrGapResultDataLoading,
  } = useHedgeRatioContext();

  const { regions } = selectedUserDetail || {};

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency, writable } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
    setIsWritableSfc(writable);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
    setIsWritableSfc,
  ]);

  const handleSelectedModelDatabase = (modelDatabase) => {
    setSelectedModelDatabase(modelDatabase);
    const modelDatabaseObject = modelDatabases?.find(
      (modelDb) => modelDb.modelName === modelDatabase,
    );
    const rawDate = modelDatabaseObject?.monthEnd;
    if (rawDate) {
      setSelectedMonthEnd(dayjs(rawDate.replace("Z", "")));
    }
  };

  const onSubmitClick = () => {
    const selectedModelObject = modelDatabases?.find(
      (modelDb) => modelDb.modelName === selectedModelDatabase,
    );

    const advancedNII = selectedModelObject?.advancedNII === true;

    setIsAdvancedNII(advancedNII);

    handleSubmit({
      selectedBusinessEntityID,
      selectedModelDatabase,
      selectedMonthEnd: selectedModelObject?.monthEnd
        ? dayjs(selectedModelObject.monthEnd.replace("Z", ""))
        : null,
      advancedNII,
      selectedFigureDivisor,
      selectedDecimalsIn,
    });
  };
  return (
    <>
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the {isAdvancedNII ? "Advanced NII" : "NII"}{" "}
        Sensitivity Report (Modeler Global View) based on the selected filters.
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"model-database"}>Model Database</InputLabel>
          <Autocomplete
            options={mappedModelDatabases}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedModelDatabases.find(
                (item) => item.id === selectedModelDatabase,
              ) || null
            }
            onChange={(_, newValue) => {
              handleSelectedModelDatabase(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Model Database"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>

        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onSubmitClick}
          sx={{
            fontSize: "0.8rem",
            boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
            height: "38px",
            mt: 3,
          }}
          disabled={
            selectedUserDetailLoading ||
            hrFiltersDataLoading ||
            hrGapResultDataLoading ||
            !selectedBusinessEntityID ||
            !selectedModelDatabase
          }
        >
          Submit
        </Button>
      </Grid>
    </>
  );
};

export default NIISensitivityModelerGlobalFilters;
